/**
 * The one thing a configuration file cannot be: proof that the printer works.
 *
 * This lived inside `settings.tsx` as a closure, which made it reachable only
 * by somebody standing at the machine — press-and-hold the corner, prove a PIN,
 * find the button. That is the right way to *check* a printer and the wrong way
 * to *diagnose* one, because the person who needs the answer is usually on the
 * telephone to the shop rather than in it. It is a module so the console can
 * ask for it too (`test-print-requested`), and so both callers print the same
 * slip: a support call comparing two different test pages is a support call
 * that learns nothing.
 *
 * **Ө and Ү are the test.** No ESC/POS code page anybody's hardware ships
 * contains them, so a machine that has quietly fallen back to text mode prints
 * those product names wrong and a machine in raster mode prints them right.
 * That is why `SAMPLE_SALES` puts them in the names on purpose, and why
 * `packages/receipt` names the four letters in its own tests.
 *
 * **It prints every section, not a minimal one.** The slip used to be a single
 * line at ₮1,100 with no fiscal block, no company, no discount and no change —
 * which proved the printer and almost nothing about the receipt. The sections
 * a shop actually rings up about are the conditional ones: a QR that will not
 * resolve, a 33-digit ДДТД that overruns its row, a logo that comes out a black
 * rectangle. So it draws `sampleSale('everything')` — the same contrived sale
 * the console's designer previews and a proof prints — where every switch in
 * the arrangement has something to put on the paper.
 *
 * **Three callers now, and the third is in Rust.** The shell's setup window has
 * the same button, for the machine whose page will not load — and it prints
 * this slip rather than one of its own, by asking for the bytes rather than
 * composing them. `watchShellTestPrints` is that answer; `shell_test_print`
 * says why the shell still keeps an ASCII slip behind it.
 */
import type { Slip } from '@mgc/receipt'
import { buildSlip, sampleSale, SAMPLE_BRANCH } from '@mgc/receipt'
import { printSlip, slipBytes } from '@/shell/print'
import { listen, testSlipDrawn } from '@/shell/native'
import { record } from './diagnostics'
import { templateInHand } from './receipt-template'
import { useSession } from '@/store/session'

export interface TestPrintOutcome {
  /** What actually printed it, which is the answer the caller wanted. */
  readonly via: 'thermal' | 'browser'
  /** One line, already written for a person reading a support bundle. */
  readonly summary: string
}

export async function runTestPrint(): Promise<TestPrintOutcome> {
  const outcome = await printSlip(testSlip())
  return {
    via: outcome.via,
    summary: outcome.via === 'thermal' ? `thermal (${outcome.mode})` : `browser (${outcome.because})`
  }
}

/**
 * The slip itself, so something other than a printer can have it.
 *
 * Separate from `runTestPrint` for the shell, which wants the paper drawn here
 * and sent there — and keeping one builder is the point: a support call
 * comparing the settings screen's slip against the setup window's is a support
 * call that learns nothing if they are two different pages.
 *
 * **This shop's details, the sample's where it has none.** The order is the
 * shared sample and the branch is not, which is the same split
 * `receipt-proof.ts` describes from the other side: a proof answers «is my
 * arrangement right», so it draws the sample shop the console's preview drew; a
 * test print proves *this machine*, where the real shop name and the real WiFi
 * are the point. The sample fills the gaps rather than leaving them blank,
 * because a section that printed nothing has told an integrator nothing — a
 * kiosk that has not loaded its branch config yet must still put a WiFi row on
 * the paper.
 */
export function testSlip(): Slip {
  const branch = useSession.getState().branch
  return buildSlip(
    // Every conditional section has something to say: a discount, change, a
    // company, an add-on, a note, and all three of e-Barimt's — the lottery
    // number, the 33-digit ДДТД and the QR, at the sizes the real ones come
    // back. Which of them reach the paper is still the template's decision —
    // `fiscalBillId` is off in `DEFAULT_ENTRIES` — and that is the point of
    // drawing against `templateInHand()`. See `SAMPLE_SALES`.
    sampleSale('everything').order,
    {
      name: branch?.name ?? SAMPLE_BRANCH.name,
      address: branch?.address ?? SAMPLE_BRANCH.address,
      wifiName: branch?.wifiName ?? SAMPLE_BRANCH.wifiName,
      wifiPassword: branch?.wifiPassword ?? SAMPLE_BRANCH.wifiPassword
    },
    // The shop's own arrangement, not the built-in one: a test print that
    // proves a printer should prove the receipt that printer is going to be
    // asked for — including whether its logo comes out.
    templateInHand()
  )
}

/**
 * The setup window's test print, answered from here.
 *
 * The shell cannot draw a receipt — `packages/receipt` runs in the page — so
 * the window emits a nonce and waits four seconds for the bytes. What this
 * owes it is an answer either way: a failure reported as `null` is the shell's
 * ASCII fallback printed now rather than in four seconds, and an integrator
 * watching a printer plate notices the difference.
 *
 * Never throws, and never falls back to `window.print()`. Nobody asked this
 * page for paper; a print dialog opening over a selling kiosk because somebody
 * pressed a button in another window is the wrong answer to every question.
 */
export function watchShellTestPrints(): () => void {
  const pending = listen<{ id: number }>('shell://test-slip-requested', (payload) => {
    void slipBytes(testSlip())
      .then((bytes) => testSlipDrawn(payload.id, bytes))
      .catch((error: unknown) => {
        record('warn', `the shell asked for a test slip and this page could not draw one: ${String(error)}`)
        return testSlipDrawn(payload.id, null)
      })
      .catch(() => {})
  })
  return () => {
    void pending.then((off) => off()).catch(() => {})
  }
}
